import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArrowRight, ArrowLeft } from 'lucide-react';

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

export const OnboardingModal = ({ isOpen, onClose, onComplete }: OnboardingModalProps) => {
  const [currentStep, setCurrentStep] = useState(0);

  // Reset to first slide whenever modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  // Content for step 4 - Creating and Managing Events
  const getStep4Content = () => (
    <div className="space-y-3">
      <p className="text-xs sm:text-sm text-muted-foreground text-center">Organize your checklists by creating custom events</p>
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        <div className="p-3 rounded-lg border border-border">
          <h4 className="text-sm font-display font-semibold mb-1.5">Creating Events</h4>
          <ul className="space-y-0.5 text-xs text-muted-foreground font-body leading-relaxed list-disc pl-4">
            <li>Click "+" to add events</li>
            <li>Name your events</li>
            <li>Edit by clicking title</li>
          </ul>
        </div>
        <div className="p-3 rounded-lg border border-border">
          <h4 className="text-sm font-display font-semibold mb-1.5">Event Features</h4>
          <ul className="space-y-0.5 text-xs text-muted-foreground font-body leading-relaxed list-disc pl-4">
            <li>Copy, share, and print</li>
            <li>Collapse/expand events</li>
            <li>Set countdown timers</li>
          </ul>
        </div>
      </div>
    </div>
  );

  // Content for step 5 - Working with Checklists
  const getStep5Content = () => (
    <div className="space-y-3">
      <p className="text-xs sm:text-sm text-muted-foreground font-body leading-relaxed text-center">Master the checklist interface and item management</p>
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        <div className="p-3 rounded-lg border border-border">
          <h4 className="text-sm font-display font-semibold mb-1.5">Editing Checklists</h4>
          <ul className="space-y-0.5 text-xs text-muted-foreground font-body leading-relaxed list-disc pl-4">
            <li>Click tile to open</li>
            <li>Edit name by clicking it</li>
            <li>Add up to 25 items</li>
            <li>Drag to reorder</li>
          </ul>
        </div>
        <div className="p-3 rounded-lg border border-border">
          <h4 className="text-sm font-display font-semibold mb-1.5">S-B-R Example</h4>
          <ul className="space-y-0.5 text-xs text-muted-foreground font-body leading-relaxed list-disc pl-4">
            <li>S: I have my wetsuit</li>
            <li>B: Staged for packing</li>
            <li>R: Packed in bag</li>
          </ul>
        </div>
      </div>
    </div>
  );

  // Content for step 6 - Advanced Features
  const getStep6Content = () => (
    <div className="space-y-3">
      <p className="text-xs sm:text-sm text-muted-foreground font-body leading-relaxed text-center">Make the most of your race preparation system</p>
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        <div className="p-3 rounded-lg border border-border">
          <h4 className="text-sm font-display font-semibold mb-1.5">Sharing</h4>
          <ul className="space-y-0.5 text-xs text-muted-foreground font-body leading-relaxed list-disc pl-4">
            <li>Share with partners</li>
            <li>Print for offline use</li>
            <li>Copy between events</li>
          </ul>
        </div>
        <div className="p-3 rounded-lg border border-border">
          <h4 className="text-sm font-display font-semibold mb-1.5">Mobile</h4>
          <ul className="space-y-0.5 text-xs text-muted-foreground font-body leading-relaxed list-disc pl-4">
            <li>Full-screen editing</li>
            <li>Quick search</li>
            <li>Access anywhere</li>
          </ul>
        </div>
      </div>
      <div className="p-3 rounded-lg border border-border text-center">
        <p className="text-sm sm:text-base font-display font-semibold text-foreground leading-relaxed">
          Start with our sample checklist, explore event templates, or build from scratch!
        </p>
      </div>
    </div>
  );

  const onboardingSteps = [
    {
      title: "Welcome to Sink Bike Run!",
      content: (
        <div className="text-center space-y-3">
          <p className="text-xs sm:text-sm font-body leading-relaxed">
            Your complete triathlon preparation companion, designed to make race day prep simple and stress-free.
          </p>
          <p className="text-xs sm:text-sm text-muted-foreground font-body leading-relaxed">
            Whether you're tackling your first sprint triathlon or preparing for a long-distance race, our checklist system—with event-specific templates and flexible customization—ensures you never forget the essentials.
          </p>
        </div>
      )
    },
    {
      title: "How Our Checklist System Works",
      content: (
        <div className="space-y-3">
          <p className="text-xs sm:text-sm text-muted-foreground font-body leading-relaxed text-center">Three-Column Organization</p>
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <div className="text-center p-3 border border-border rounded-lg">
              <h4 className="text-sm font-display font-semibold mb-1">Secured (S)</h4>
              <p className="text-xs text-muted-foreground font-body leading-relaxed">You possess the item</p>
            </div>
            <div className="text-center p-3 border border-border rounded-lg">
              <h4 className="text-sm font-display font-semibold mb-1">Bundled (B)</h4>
              <p className="text-xs text-muted-foreground font-body leading-relaxed">Staged for packing</p>
            </div>
            <div className="text-center p-3 border border-border rounded-lg">
              <h4 className="text-sm font-display font-semibold mb-1">Ready (R)</h4>
              <p className="text-xs text-muted-foreground font-body leading-relaxed">Physically packed</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Flexible Organization",
      content: (
        <div className="space-y-3">
          <p className="text-center text-xs sm:text-sm text-muted-foreground font-body leading-relaxed">
            Organize checklists by race phases, timeline, category, or location
          </p>
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <div className="p-3 border border-border rounded-lg">
              <h4 className="text-sm font-display font-semibold mb-1">By Timeline</h4>
              <p className="text-xs text-muted-foreground font-body leading-relaxed">Days before, race morning</p>
            </div>
            <div className="p-3 border border-border rounded-lg">
              <h4 className="text-sm font-display font-semibold mb-1">By Category</h4>
              <p className="text-xs text-muted-foreground font-body leading-relaxed">Nutrition, gear, logistics</p>
            </div>
            <div className="p-3 border border-border rounded-lg">
              <h4 className="text-sm font-display font-semibold mb-1">By Race Phase</h4>
              <p className="text-xs text-muted-foreground font-body leading-relaxed">Swim, bike, run, transitions</p>
            </div>
            <div className="p-3 border border-border rounded-lg">
              <h4 className="text-sm font-display font-semibold mb-1">By Location</h4>
              <p className="text-xs text-muted-foreground font-body leading-relaxed">Home prep, hotel, transition</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Creating and Managing Events",
      content: getStep4Content()
    },
    {
      title: "Working with Checklists",
      content: getStep5Content()
    },
    {
      title: "Ready to Get Started?",
      content: getStep6Content()
    }
  ];

  const handleNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('onboarding_completed', 'true');
    setCurrentStep(0); // Reset to step 1 for next time
    onClose();
  };

  const handleClose = () => {
    setCurrentStep(0); // Reset to step 1 for next time
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[85vh] p-4 sm:p-6 flex flex-col gap-0">
        <div className="flex flex-col h-full pt-8 sm:pt-2">
          {/* Progress Bar */}
          <div className="space-y-2 flex-shrink-0 mb-4">
            <div className="flex justify-center">
              <span className="text-xs text-muted-foreground font-body">Step {currentStep + 1} of {onboardingSteps.length}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-1">
              <div 
                className="bg-foreground h-1 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / onboardingSteps.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Step Content - Fixed height for all slides */}
          <div className="flex-1 min-h-[340px] sm:min-h-[320px]">
            <div className="space-y-3 sm:space-y-4">
              <h2 className="text-lg sm:text-xl md:text-2xl font-display font-bold text-center px-2">
                {onboardingSteps[currentStep].title}
              </h2>
              <div className="px-1">
                {onboardingSteps[currentStep].content}
              </div>
            </div>
          </div>

          {/* Navigation - Fixed at bottom */}
          <div className="flex justify-between items-center pt-3 sm:pt-4 border-t flex-shrink-0 mt-3">
            <Button 
              variant="outline" 
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="flex items-center gap-1 h-9 px-3 text-sm font-body"
              size="sm"
            >
              <ArrowLeft className="h-3 w-3" />
              <span className="hidden sm:inline">Previous</span>
              <span className="sm:hidden">Prev</span>
            </Button>

            <Button 
              variant="ghost" 
              onClick={handleSkip} 
              className="text-muted-foreground h-9 px-3 text-sm font-body"
              size="sm"
            >
              Skip
            </Button>

            <Button 
              variant="accent"
              onClick={handleNext}
              className="flex items-center gap-1 h-9 px-3 text-sm font-body"
              size="sm"
            >
              {currentStep === onboardingSteps.length - 1 ? 'Start' : 'Next'}
              <ArrowRight className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};